import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useRef, useState, type ChangeEvent } from "react";
import {
  User as UserIcon,
  CalendarDays,
  Settings as SettingsIcon,
  Pencil,
  MapPin,
  Clock,
  Bell,
  Lock,
  LogOut,
  CheckCircle2,
  XCircle,
  Hourglass,
  CreditCard,
  Heart,
  FileText,
  QrCode,
  Printer,
  Star,
  Trash2,
  Download,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import space1 from "@/assets/space-1.webp";
import space2 from "@/assets/space-2.webp";
import space3 from "@/assets/space-3.webp";
import space4 from "@/assets/space-4.webp";

import { redirect } from "@tanstack/react-router";
import { getSession } from "@/lib/session";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/dashboard")({
  beforeLoad: ({ location }) => {
    const session = getSession();
    if (!session) {
      throw redirect({ to: "/login", search: { redirect: location.href } as never });
    }
  },
  head: () => ({
    meta: [
      { title: "Your dashboard — Spayzo" },
      { name: "description", content: "Manage your Spayzo profile, bookings, reviews and account settings in one place." },
      { property: "og:title", content: "Your dashboard — Spayzo" },
      { property: "og:description", content: "Profile, bookings and settings in one place." },
      { property: "og:url", content: "https://spayzo.lovable.app/dashboard" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/dashboard" }],
  }),
  component: DashboardPage,
});

type Section = "profile" | "bookings" | "reviews" | "settings";

const NAV: { id: Section; labelKey: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "profile", labelKey: "pages.dashboard.nav.profile", icon: UserIcon },
  { id: "bookings", labelKey: "pages.dashboard.nav.bookings", icon: CalendarDays },
  { id: "reviews", labelKey: "pages.dashboard.nav.reviews", icon: Star },
  { id: "settings", labelKey: "pages.dashboard.nav.settings", icon: SettingsIcon },
];

type Review = {
  id: string;
  space: string;
  image: string;
  date: string;
  rating: number;
  text: string;
};

const INITIAL_REVIEWS: Review[] = [
  {
    id: "r1",
    space: "Atelier Nord",
    image: space3,
    date: "Apr 05, 2026",
    rating: 5,
    text: "Beautiful light, super smooth check-in. Would book again for our next shoot.",
  },
  {
    id: "r2",
    space: "Cedar Chalet",
    image: space4,
    date: "Mar 20, 2026",
    rating: 4,
    text: "Peaceful setting and generous space. Wi-Fi could be a touch faster.",
  },
  {
    id: "r3",
    space: "Sunlit Creative Studio",
    image: space1,
    date: "Feb 12, 2026",
    rating: 5,
    text: "Perfect studio for our workshop — Mara was incredibly helpful throughout.",
  },
];


type Booking = {
  id: string;
  space: string;
  location: string;
  address: string;
  image: string;
  date: string;
  time: string;
  guests: number;
  hours: number;
  rate: number;
  total: number;
  reference: string;
  host: string;
  paymentMethod: string;
  status: "confirmed" | "completed" | "refunded" | "cancelled";
};

const UPCOMING: Booking[] = [
  {
    id: "b1",
    space: "Sunlit Creative Studio",
    location: "SoHo, New York",
    address: "112 Greene St, New York, NY 10012",
    image: space1,
    date: "May 12, 2026",
    time: "10:00 — 14:00",
    guests: 6,
    hours: 4,
    rate: 85,
    total: 340,
    reference: "HRL-2026-0512-A",
    host: "Mara Lindqvist",
    paymentMethod: "Visa ending 4242",
    status: "confirmed",
  },
  {
    id: "b2",
    space: "The Glass Loft",
    location: "Williamsburg, Brooklyn",
    address: "88 N 6th St, Brooklyn, NY 11249",
    image: space2,
    date: "May 24, 2026",
    time: "18:00 — 22:00",
    guests: 12,
    hours: 4,
    rate: 120,
    total: 480,
    reference: "HRL-2026-0524-B",
    host: "Daniel Reyes",
    paymentMethod: "Mastercard ending 1881",
    status: "confirmed",
  },
];

const PAST: Booking[] = [
  {
    id: "b3",
    space: "Atelier Nord",
    location: "Greenpoint, Brooklyn",
    address: "55 Franklin St, Brooklyn, NY 11222",
    image: space3,
    date: "Apr 02, 2026",
    time: "09:00 — 12:00",
    guests: 4,
    hours: 3,
    rate: 70,
    total: 210,
    reference: "HRL-2026-0402-C",
    host: "Iben Holm",
    paymentMethod: "Visa ending 4242",
    status: "completed",
  },
  {
    id: "b4",
    space: "Cedar Chalet",
    location: "Hudson Valley",
    address: "12 Pine Ridge Rd, Cold Spring, NY 10516",
    image: space4,
    date: "Mar 18, 2026",
    time: "All day",
    guests: 8,
    hours: 10,
    rate: 60,
    total: 600,
    reference: "HRL-2026-0318-D",
    host: "Theo Larsen",
    paymentMethod: "Apple Pay",
    status: "cancelled",
  },
];

function DashboardPage() {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section>("profile");

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <SiteHeader />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("pages.dashboard.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("pages.dashboard.subtitle")}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <nav className="rounded-2xl border bg-card p-2 shadow-soft">
              {NAV.map((item) => {
                const Icon = item.icon;
                const active = section === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setSection(item.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                      active
                        ? "bg-[#5958F3] text-white"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {t(item.labelKey)}
                  </button>
                );
              })}
              <Separator className="my-2" />
              <Link
                to="/favorites"
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Heart className="h-4 w-4" />
                {t("pages.dashboard.nav.favorites")}
              </Link>
              <Link
                to="/subscriptions"
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <CreditCard className="h-4 w-4" />
                {t("pages.dashboard.nav.subscriptions")}
              </Link>
              <Link
                to="/login"
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <LogOut className="h-4 w-4" />
                {t("pages.dashboard.nav.signOut")}
              </Link>
            </nav>
          </aside>

          {/* Main */}
          <section className="min-w-0">
            {section === "profile" && <ProfileSection />}
            {section === "bookings" && <BookingsSection />}
            {section === "reviews" && <ReviewsSection />}
            {section === "settings" && <SettingsSection />}
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function ProfileSection() {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("Alex Morgan");
  const [bio, setBio] = useState(t("pages.dashboard.profile.bioValue"));
  const [avatar, setAvatar] = useState<string>(() =>
    typeof window !== "undefined" ? localStorage.getItem("spayzo.avatar") ?? "" : "",
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setAvatar(dataUrl);
      try { localStorage.setItem("spayzo.avatar", dataUrl); } catch {}
    };
    reader.readAsDataURL(file);
  };

  return (
    <Card className="shadow-soft">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="font-display text-xl">{t("pages.dashboard.profile.title")}</CardTitle>
          <CardDescription>{t("pages.dashboard.profile.desc")}</CardDescription>
        </div>
        <Button
          variant={editing ? "default" : "outline"}
          size="sm"
          onClick={() => setEditing((v) => !v)}
        >
          <Pencil className="h-4 w-4" />
          {editing ? t("pages.dashboard.profile.save") : t("pages.dashboard.profile.edit")}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          <div className="flex flex-col items-center gap-3">
            <Avatar className="h-24 w-24 ring-4 ring-muted">
              <AvatarImage src={avatar} alt={name} />
              <AvatarFallback className="bg-foreground text-background font-display text-xl">
                {name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </AvatarFallback>
            </Avatar>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />
            {editing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                {t("pages.dashboard.profile.changePhoto")}
              </Button>
            )}
          </div>

          <div className="flex-1 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("pages.dashboard.profile.name")}</Label>
              {editing ? (
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
              ) : (
                <p className="text-base font-medium">{name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio">{t("pages.dashboard.profile.bio")}</Label>
              {editing ? (
                <Textarea id="bio" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} />
              ) : (
                <p className="text-sm text-muted-foreground">{bio}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="secondary" className="rounded-full">{t("pages.dashboard.profile.bookingsCount", { count: 12 })}</Badge>
              <Badge variant="secondary" className="rounded-full">{t("pages.dashboard.profile.memberSince", { year: 2024 })}</Badge>
              <Badge variant="secondary" className="rounded-full">{t("pages.dashboard.profile.verified")}</Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BookingsSection() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Booking | null>(null);
  const [invoiceBooking, setInvoiceBooking] = useState<Booking | null>(null);
  const [upcoming, setUpcoming] = useState<Booking[]>(UPCOMING);
  const [past, setPast] = useState<Booking[]>(PAST);
  const [cancelling, setCancelling] = useState<Booking | null>(null);

  const handleCancel = (booking: Booking) => {
    setUpcoming((prev) => prev.filter((b) => b.id !== booking.id));
    setPast((prev) => [{ ...booking, status: "cancelled" as const }, ...prev]);
    setCancelling(null);
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="font-display text-xl">{t("pages.dashboard.bookings.title")}</CardTitle>
        <CardDescription>{t("pages.dashboard.bookings.desc")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="upcoming">
          <TabsList className="rounded-full">
            <TabsTrigger value="upcoming" className="rounded-full px-4">
              {t("pages.dashboard.bookings.upcoming")}
            </TabsTrigger>
            <TabsTrigger value="past" className="rounded-full px-4">
              {t("pages.dashboard.bookings.past")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-4 space-y-3">
            {upcoming.length === 0 ? (
              <EmptyState label={t("pages.dashboard.bookings.noUpcoming")} />
            ) : (
              upcoming.map((b) => (
                <BookingCard
                  key={b.id}
                  booking={b}
                  onView={() => setSelected(b)}
                  onCancel={() => setCancelling(b)}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-4 space-y-3">
            {past.length === 0 ? (
              <EmptyState label={t("pages.dashboard.bookings.noPast")} />
            ) : (
              past.map((b) => (
                <BookingCard key={b.id} booking={b} onView={() => setSelected(b)} />
              ))
            )}
          </TabsContent>
        </Tabs>

        <BookingDetailDialog
          booking={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          onSeeInvoice={(b) => {
            setSelected(null);
            setInvoiceBooking(b);
          }}
        />
        <InvoiceDialog
          booking={invoiceBooking}
          open={!!invoiceBooking}
          onOpenChange={(o) => !o && setInvoiceBooking(null)}
        />
        <CancelConfirmDialog
          booking={cancelling}
          open={!!cancelling}
          onOpenChange={(o) => !o && setCancelling(null)}
          onConfirm={() => cancelling && handleCancel(cancelling)}
        />
      </CardContent>
    </Card>
  );
}

function BookingCard({
  booking,
  onView,
  onCancel,
}: {
  booking: Booking;
  onView: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-card p-3 transition-shadow hover:shadow-card sm:flex-row sm:items-center">
      <img
        src={booking.image}
        alt={booking.space}
        className="h-32 w-full rounded-xl object-cover sm:h-20 sm:w-28"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-medium">{booking.space}</h3>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {booking.location}
            </p>
          </div>
          <StatusBadge status={booking.status} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CalendarDays className="h-3.5 w-3.5" />
            {booking.date}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {booking.time}
          </span>
        </div>
      </div>
      <div className="flex gap-2 sm:flex-col">
        <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={onView}>
          {t("pages.dashboard.bookings.details")}
        </Button>
        {booking.status === "confirmed" ? (
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 sm:flex-none"
            onClick={onCancel}
          >
            {t("pages.dashboard.bookings.cancel")}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="flex-1 sm:flex-none">
            {t("pages.dashboard.bookings.rebook")}
          </Button>
        )}
      </div>
    </div>
  );
}

function BookingDetailDialog({
  booking,
  open,
  onOpenChange,
  onSeeInvoice,
}: {
  booking: Booking | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSeeInvoice: (b: Booking) => void;
}) {
  const { t } = useTranslation();
  if (!booking) return null;
  const fee = Math.round(booking.total * 0.1);
  const tax = Math.round(booking.total * 0.08);
  const grand = booking.total + fee + tax;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <img
          src={booking.image}
          alt={booking.space}
          className="h-44 w-full object-cover"
        />
        <div className="p-6">
          <DialogHeader className="space-y-2 text-start">
            <div className="flex items-start justify-between gap-3">
              <DialogTitle className="font-display text-xl">{booking.space}</DialogTitle>
              <StatusBadge status={booking.status} />
            </div>
            <DialogDescription className="flex items-center gap-1 text-xs">
              <MapPin className="h-3 w-3" />
              {booking.address}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <DetailRow label={t("pages.dashboard.bookings.date")} value={booking.date} />
            <DetailRow label={t("pages.dashboard.bookings.time")} value={booking.time} />
            <DetailRow label={t("pages.dashboard.bookings.guests")} value={t("pages.dashboard.bookings.people", { count: booking.guests })} />
            <DetailRow label={t("pages.dashboard.bookings.duration")} value={t("pages.dashboard.bookings.hoursCount", { count: booking.hours })} />
            <DetailRow label={t("pages.dashboard.bookings.reference")} value={booking.reference} />
            <DetailRow label={t("pages.dashboard.bookings.host")} value={booking.host} />
          </div>

          <Separator className="my-5" />

          <div className="space-y-1.5 text-sm">
            <Row label={<><Money value={booking.rate} /> × {t("pages.dashboard.bookings.hoursCount", { count: booking.hours })}</>} value={<Money value={booking.total} />} />
            <Row label={t("pages.dashboard.bookings.serviceFee")} value={<Money value={fee} />} />
            <Row label={t("pages.dashboard.bookings.taxes")} value={<Money value={tax} />} />
            <div className="mt-2 flex items-center justify-between border-t pt-2 font-semibold">
              <span>{t("pages.dashboard.bookings.total")}</span>
              <span><Riyal /> {grand}</span>
            </div>
            <p className="pt-2 text-xs text-muted-foreground">
              {t("pages.dashboard.bookings.paidWith", { method: booking.paymentMethod })}
            </p>
          </div>

          <DialogFooter className="mt-6 flex-row flex-wrap gap-2 sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => onSeeInvoice(booking)}
            >
              <FileText className="h-4 w-4" /> {t("pages.dashboard.bookings.seeInvoice")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => onSeeInvoice(booking)}
            >
              <Download className="h-4 w-4" /> {t("pages.dashboard.bookings.downloadInvoice")}
            </Button>
            {booking.status !== "confirmed" && (
              <Button size="sm" className="flex-1 sm:flex-none">
                {t("pages.dashboard.bookings.rebook")}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDialog({
  booking,
  open,
  onOpenChange,
}: {
  booking: Booking | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  if (!booking) return null;
  const fee = Math.round(booking.total * 0.1);
  const tax = Math.round(booking.total * 0.08);
  const grand = booking.total + fee + tax;
  const invoiceNo = `INV-${booking.reference}`;
  const qrPayload = `SPAYZO|${invoiceNo}|${booking.reference}|SAR ${grand}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(
    qrPayload,
  )}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="bg-foreground text-background p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest opacity-70">Spayzo</p>
              <h2 className="mt-1 font-display text-xl font-bold">{t("pages.dashboard.bookings.invoice")}</h2>
              <p className="mt-0.5 text-xs opacity-80">{invoiceNo}</p>
            </div>
            <span className="rounded-full bg-background/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider">
              {t("pages.dashboard.bookings.paid")}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <DetailRow label={t("pages.dashboard.bookings.bookingRef")} value={booking.reference} />
            <DetailRow label={t("pages.dashboard.bookings.date")} value={booking.date} />
            <DetailRow label={t("pages.dashboard.bookings.space")} value={booking.space} />
            <DetailRow label={t("pages.dashboard.bookings.host")} value={booking.host} />
            <DetailRow label={t("pages.dashboard.bookings.time")} value={booking.time} />
            <DetailRow label={t("pages.dashboard.bookings.guests")} value={t("pages.dashboard.bookings.people", { count: booking.guests })} />
          </div>

          <Separator className="my-5" />

          <div className="space-y-1.5 text-sm">
            <Row label={<><Money value={booking.rate} /> × {t("pages.dashboard.bookings.hoursShort", { count: booking.hours })}</>} value={<Money value={booking.total} />} />
            <Row label={t("pages.dashboard.bookings.serviceFee")} value={<Money value={fee} />} />
            <Row label={t("pages.dashboard.bookings.vat")} value={<Money value={tax} />} />
            <div className="mt-2 flex items-center justify-between border-t pt-2 font-semibold">
              <span>{t("pages.dashboard.bookings.totalPaid")}</span>
              <span><Riyal /> {grand}</span>
            </div>
            <p className="pt-1 text-xs text-muted-foreground">
              {t("pages.dashboard.bookings.paidWith", { method: booking.paymentMethod })}
            </p>
          </div>

          <Separator className="my-5" />

          <div className="flex items-center gap-4 rounded-2xl border bg-muted/30 p-4">
            <div className="flex h-[120px] w-[120px] shrink-0 items-center justify-center rounded-xl bg-background p-2 shadow-soft">
              <img
                src={qrUrl}
                alt={t("pages.dashboard.bookings.qrAlt", { no: invoiceNo })}
                width={104}
                height={104}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="min-w-0 text-xs">
              <div className="flex items-center gap-1 font-semibold text-foreground">
                <QrCode className="h-3.5 w-3.5" /> {t("pages.dashboard.bookings.scanToVerify")}
              </div>
              <p className="mt-1 text-muted-foreground">
                {t("pages.dashboard.bookings.qrHint")}
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6 flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4" /> {t("pages.dashboard.bookings.print")}
            </Button>
            <Button size="sm" className="flex-1 sm:flex-none" onClick={() => onOpenChange(false)}>
              {t("pages.dashboard.bookings.done")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CancelConfirmDialog({
  booking,
  open,
  onOpenChange,
  onConfirm,
}: {
  booking: Booking | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">{t("pages.dashboard.bookings.cancelTitle")}</DialogTitle>
          <DialogDescription>
            {t("pages.dashboard.bookings.cancelQuestion", { space: booking.space, date: booking.date }).replace(/<\/?1>/g, "")}{" "}
            {t("pages.dashboard.bookings.cancelDesc")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 flex-row gap-2 sm:justify-end">
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => onOpenChange(false)}>
            {t("pages.dashboard.bookings.keepBooking")}
          </Button>
          <Button variant="destructive" size="sm" className="flex-1 sm:flex-none" onClick={onConfirm}>
            {t("pages.dashboard.bookings.cancelBooking")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium">{value}</p>
    </div>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-muted-foreground">
      <span>{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: Booking["status"] }) {
  const { t } = useTranslation();
  const map = {
    confirmed: { icon: CheckCircle2, className: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
    refunded: { icon: Hourglass, className: "bg-blue-500/10 text-blue-700 dark:text-blue-400" },
    completed: { icon: CheckCircle2, className: "bg-muted text-muted-foreground" },
    cancelled: { icon: XCircle, className: "bg-destructive/10 text-destructive" },
  } as const;
  const { icon: Icon, className } = map[status];
  const label = t(`pages.dashboard.status.${status}`);
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", className)}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function EmptyState({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed py-12 text-center">
      <CalendarDays className="h-8 w-8 text-muted-foreground" />
      <p className="mt-2 text-sm text-muted-foreground">{label}</p>
      <Button asChild variant="outline" size="sm" className="mt-4">
        <Link to="/discover">{t("pages.dashboard.bookings.discover")}</Link>
      </Button>
    </div>
  );
}

function SettingsSection() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <Lock className="h-4 w-4" />
            {t("pages.dashboard.settings.passwordTitle")}
          </CardTitle>
          <CardDescription>{t("pages.dashboard.settings.passwordDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="current">{t("pages.dashboard.settings.current")}</Label>
              <Input id="current" type="password" placeholder="••••••••" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">{t("pages.dashboard.settings.newPassword")}</Label>
              <Input id="new" type="password" placeholder="••••••••" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">{t("pages.dashboard.settings.confirmPassword")}</Label>
              <Input id="confirm" type="password" placeholder="••••••••" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button>{t("pages.dashboard.settings.updatePassword")}</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <Bell className="h-4 w-4" />
            {t("pages.dashboard.settings.notifTitle")}
          </CardTitle>
          <CardDescription>{t("pages.dashboard.settings.notifDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          <NotifRow
            title={t("pages.dashboard.settings.bookingUpdates")}
            desc={t("pages.dashboard.settings.bookingUpdatesDesc")}
            defaultChecked
          />
          <NotifRow
            title={t("pages.dashboard.settings.promotions")}
            desc={t("pages.dashboard.settings.promotionsDesc")}
          />
          <NotifRow
            title={t("pages.dashboard.settings.productNews")}
            desc={t("pages.dashboard.settings.productNewsDesc")}
            defaultChecked
          />
        </CardContent>
      </Card>
    </div>
  );
}

function NotifRow({
  title,
  desc,
  defaultChecked,
}: {
  title: string;
  desc: string;
  defaultChecked?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <Switch defaultChecked={defaultChecked} />
    </div>
  );
}

function ReviewsSection() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>(INITIAL_REVIEWS);
  const [editing, setEditing] = useState<Review | null>(null);
  const [deleting, setDeleting] = useState<Review | null>(null);
  const [draftRating, setDraftRating] = useState(5);
  const [draftText, setDraftText] = useState("");

  const openEdit = (r: Review) => {
    setEditing(r);
    setDraftRating(r.rating);
    setDraftText(r.text);
  };

  const saveEdit = () => {
    if (!editing) return;
    setReviews((prev) =>
      prev.map((r) => (r.id === editing.id ? { ...r, rating: draftRating, text: draftText } : r)),
    );
    setEditing(null);
  };

  const confirmRemove = () => {
    if (!deleting) return;
    setReviews((prev) => prev.filter((r) => r.id !== deleting.id));
    setDeleting(null);
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="font-display text-xl">{t("pages.dashboard.reviews.title")}</CardTitle>
        <CardDescription>{t("pages.dashboard.reviews.desc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {reviews.length === 0 ? (
          <EmptyState label={t("pages.dashboard.reviews.empty")} />
        ) : (
          reviews.map((r) => (
            <div
              key={r.id}
              className="flex flex-col gap-4 rounded-2xl border bg-card p-3 sm:flex-row sm:items-start"
            >
              <img
                src={r.image}
                alt={r.space}
                className="h-32 w-full rounded-xl object-cover sm:h-20 sm:w-28"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate font-medium">{r.space}</h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">{r.date}</p>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          "h-4 w-4",
                          i < r.rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground/40",
                        )}
                      />
                    ))}
                  </div>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{r.text}</p>
              </div>
              <div className="flex gap-2 sm:flex-col">
                <Button variant="outline" size="sm" onClick={() => openEdit(r)}>
                  <Pencil className="h-4 w-4" /> {t("pages.dashboard.reviews.edit")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleting(r)}>
                  <Trash2 className="h-4 w-4" /> {t("pages.dashboard.reviews.delete")}
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t("pages.dashboard.reviews.editTitle")}</DialogTitle>
            <DialogDescription>{editing?.space}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">{t("pages.dashboard.reviews.rating")}</Label>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => {
                  const v = i + 1;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setDraftRating(v)}
                      className="p-1"
                    >
                      <Star
                        className={cn(
                          "h-6 w-6 transition-colors",
                          v <= draftRating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground/40",
                        )}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="review-text">{t("pages.dashboard.reviews.review")}</Label>
              <Textarea
                id="review-text"
                rows={4}
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t("pages.dashboard.reviews.cancel")}
            </Button>
            <Button onClick={saveEdit}>{t("pages.dashboard.reviews.saveChanges")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">{t("pages.dashboard.reviews.deleteTitle")}</DialogTitle>
            <DialogDescription>
              {t("pages.dashboard.reviews.deleteDesc", { space: deleting?.space ?? "" })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex-row gap-2 sm:justify-end">
            <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => setDeleting(null)}>
              {t("pages.dashboard.reviews.keepReview")}
            </Button>
            <Button variant="destructive" size="sm" className="flex-1 sm:flex-none" onClick={confirmRemove}>
              {t("pages.dashboard.reviews.deleteReview")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

